import type { ReactNode, RefObject } from "react";
import { useSettleGuard } from "../../hooks/useSettleGuard";
import { cardKeyAct, pickText, type Pick } from "./shared";

// The question cards' answer box, their one-tap Yes and the rules both read
// (0074): the box with its act row, the confirm option's Yes, the one test
// that an answer is an option's text, and the routing id a saved answer
// carries.

// 0074: the one test that an answer is an option's text (pickText): both sides
// trimmed, and never a blank answer, so a served value with surrounding
// whitespace still reads as its option. The Yes's hold and its chosen state,
// the routing id Save & next posts, each option's selected state and when the
// box opens all read it.
export function isOptionText(answer: string, optionText: string): boolean {
  const t = answer.trim();
  return t !== "" && t === optionText.trim();
}

// 0131-T7, 0074: the routing id an answer posts with - the id of the first
// served routing pick (one with a pick id) whose text the answer is
// (isOptionText); "" for any other answer.
export function routingPickOf(picks: readonly Pick[], answer: string): string {
  return picks.find((p) => p.pickId !== "" && isOptionText(answer, pickText(p)))?.pickId ?? "";
}

// 0074: the confirm option's one-tap Yes. It reads the label it is given; while
// its own post is out it reads saving… and stays focusable (aria-disabled,
// never disabled), so a retry after a failure is one Enter. Held over an edit
// it is hidden in its slot (invisible: not shown, not focusable, and nothing
// moves); gated (the mount gate, another post of the card) it is dimmed in
// place. Chosen, it shows as a picked option does: pressed, with the selected
// ring (absent, it is not chosen). Until it has settled it takes no tap or key
// (useSettleGuard, its clock restarted when `settleKey` changes), unchanged on
// screen. `describedBy` names the element that tells this Yes apart by ear.
export function ConfirmYesButton({
  posting,
  held,
  gated,
  chosen = false,
  label,
  onYes,
  describedBy,
  settleKey,
}: {
  posting: boolean;
  held: boolean;
  gated: boolean;
  chosen?: boolean;
  label: string;
  onYes: () => void;
  describedBy?: string;
  settleKey?: string;
}) {
  const guard = useSettleGuard({ resetKey: settleKey });
  return (
    <button
      type="button"
      data-testid="confirm-yes"
      onClick={() => guard.onClick(onYes)}
      onPointerDown={guard.onPointerDown}
      onPointerUp={guard.onPointerUp}
      onPointerCancel={guard.onPointerCancel}
      // A held key activates the Yes once: its auto-repeats are not
      // presses. Before it settles no key press activates it.
      onKeyDown={guard.onKeyDown}
      disabled={gated || held}
      aria-disabled={posting || undefined}
      aria-pressed={chosen || undefined}
      aria-describedby={describedBy}
      className={`inline-flex w-full min-h-11 items-center justify-center break-words rounded-md bg-accent px-4 py-2 text-sm font-medium text-ink-950 transition-colors disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0 sm:w-auto ${
        posting ? "cursor-not-allowed opacity-40" : "hover:bg-accent-glow"
      }${chosen ? " ring-2 ring-accent/60 ring-offset-2 ring-offset-ink-950" : ""}${held ? " invisible" : ""}`}
    >
      {posting ? "saving…" : label}
    </button>
  );
}

// The question card's answer box and its act row: the box (when open) with its
// label and textarea, then the row with its status and its act. The optional
// props are the shared confirm card's (0074) and change nothing when absent:
// `submitKeyInside` binds Cmd/Ctrl+Enter to the act inside the block only, and
// never past the act's own disabled state; `tapTarget` makes the act 44 px
// tall below `sm`; no `status` renders no status span; `settleKey` gives the
// act the settle guard, its clock restarted when the key changes.
export function AnswerBlock({
  hasOptions,
  boxOpen,
  draft,
  onDraftChange,
  pending,
  textareaRef,
  status,
  act,
  onAct,
  actDisabled,
  submitKeyInside = false,
  tapTarget = false,
  settleKey,
}: {
  // The card shows options above the block.
  hasOptions: boolean;
  boxOpen: boolean;
  draft: string;
  onDraftChange: (value: string) => void;
  // A post of the card is out: the box is read-only and its border quiet.
  pending: boolean;
  textareaRef: RefObject<HTMLTextAreaElement>;
  status?: ReactNode;
  act: ReactNode;
  onAct: () => void;
  actDisabled: boolean;
  submitKeyInside?: boolean;
  tapTarget?: boolean;
  settleKey?: string;
}) {
  const guard = useSettleGuard({ resetKey: settleKey });
  const guarded = settleKey !== undefined;
  return (
    <div
      className={hasOptions ? "mt-6" : "mt-10"}
      onKeyDown={
        submitKeyInside
          ? (e) => {
              if (cardKeyAct(e) !== "submit") return;
              e.preventDefault();
              if (!actDisabled) onAct();
            }
          : undefined
      }
    >
      {boxOpen && (
        <>
          <label className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-3 block">
            Your answer
          </label>
          <div
            className={`relative rounded-lg border ${
              pending
                ? "border-ink-700"
                : "border-ink-700 hover:border-ink-600 focus-within:border-accent/60"
            } bg-ink-900/60 transition-colors`}
          >
            <textarea
              ref={textareaRef}
              rows={5}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              disabled={pending}
              placeholder="Type your answer. Plain English is fine."
              className="w-full resize-none bg-transparent px-5 py-4 text-base text-ink-100 placeholder:text-ink-500 focus:outline-none disabled:opacity-60"
            />
          </div>
        </>
      )}
      {/* Save & next: below the box, or below the options when it is
          closed (a playback's confirm option is its own act, above them,
          0074). One status beside it: Saved, or the save shortcut. */}
      <div
        data-testid="answer-act-row"
        className={`flex flex-wrap items-center justify-between gap-3 ${boxOpen ? "mt-3" : ""}`}
      >
        {status !== undefined && <span className="font-mono text-[11px] text-ink-400">{status}</span>}
        <button
          type="button"
          onClick={guarded ? () => guard.onClick(onAct) : onAct}
          onPointerDown={guarded ? guard.onPointerDown : undefined}
          onPointerUp={guarded ? guard.onPointerUp : undefined}
          onPointerCancel={guarded ? guard.onPointerCancel : undefined}
          onKeyDown={guarded ? guard.onKeyDown : undefined}
          disabled={actDisabled}
          className={`ml-auto inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-1.5 text-sm font-medium text-ink-950 hover:bg-accent-glow transition-colors disabled:cursor-not-allowed disabled:opacity-40${
            tapTarget ? " min-h-11 sm:min-h-0" : ""
          }`}
        >
          {act}
        </button>
      </div>
    </div>
  );
}
